/* eslint-disable import/extensions */
import Auth0PostLoginHandlerDefault from './implementations/auth0-post-login-handler-default.mjs';
import Auth0PostLoginHandlerWebhook from './implementations/auth0-post-login-handler-webhook.mjs';
import Auth0PostLoginHandlerShipStationHome from './implementations/auth0-post-login-handler-sshome.mjs';
import Auth0PostLoginHandlerHelpCenter from './implementations/auth0-post-login-handler-help-center.mjs';
import Auth0PostLoginHandlerExternalApp from './implementations/auth0-post-login-handler-external-app.mjs';
import Auth0PostLoginHandlerAccountLinking from './implementations/auth0-post-login-handler-account-linking.mjs';
import Auth0PostLoginHandlerNewSsoUser from './implementations/auth0-post-login-handler-new-sso-user.mjs';
import Auth0PostLoginHandlerIos from './implementations/auth0-post-login-handler-ios.mjs';
import Auth0PostLoginHandlerAndroid from './implementations/auth0-post-login-handler-android.mjs';
import Auth0PostLoginHandlerShipStationApiDash from './implementations/auth0-post-login-handler-ss-api-dash.mjs';

// `crossDomainRedirect: true` declares "this handler redirects the user off the login domain
// as its final action" (or hands the browser off to another origin, e.g. the webhook form POST).
// For handlers so declared, Auth0PostLoginHandlerHelper automatically runs cross-subdomain logout
// safety cleanup after run() — no per-handler code required: auth indicator + token cookies are
// promoted to the TLD, client-scoped cookies are deleted, and the login origin's auth0-spa-js
// token cache is cleared so a logout performed at the destination fully ends the session.
// Leave it OFF for handlers that keep the user in the login flow on the login page
// (render-and-stay, mid-flow re-login, native webview handoff) — the cleanup would destroy
// state the next step of that flow still needs.
// CONTRACT: a crossDomainRedirect handler's run() must resolve immediately after initiating
// its navigation (location.href assignment or form.submit()) — the cleanup runs in the
// microtask after run() resolves, so an await of a macrotask (or a never-resolving promise)
// after the navigation would silently skip it.
const registeredHandlers = [{
  handlerName: 'Webhook Handoff',
  handlerDescription: 'Hands off post-login to an external URL when postLoginHandoffUrl is provided',
  crossDomainRedirect: true, // form-POSTs the browser off to the consuming app's handoff URL
  HandlerClass: Auth0PostLoginHandlerWebhook,
}, {
  handlerName: 'Help Center (Zendesk)',
  handlerDescription: 'Handles post login logic and redirect for logins initiated by Zendesk',
  crossDomainRedirect: true, // redirects to the v2 zendesk SSO endpoint on another subdomain
  HandlerClass: Auth0PostLoginHandlerHelpCenter,
}, {
  handlerName: 'External App',
  handlerDescription: 'Handles post login logic, user switch and logins initiated by an external app wanting to use SS creds and access',
  // no crossDomainRedirect: renders on-page UI here; its eventual navigation happens outside run()
  HandlerClass: Auth0PostLoginHandlerExternalApp,
}, {
  handlerName: 'SSO Account Linking',
  handlerDescription: 'Handles post SSO IDP login and coordinates render of linking conformation page',
  // no crossDomainRedirect: mid-flow — user stays on the login domain through the linking steps
  HandlerClass: Auth0PostLoginHandlerAccountLinking,
}, {
  handlerName: 'SSO Onboarding / Registration',
  handlerDescription: 'Handles post SSO IDP login and coordinates creation of new user then automatic login',
  // no crossDomainRedirect: mid-flow — refresh re-enters the login flow on the login domain
  HandlerClass: Auth0PostLoginHandlerNewSsoUser,
}, {
  handlerName: 'iOS App (Webview)',
  handlerDescription: 'Handles post login events for iOS app webview (fires a login complete event into iOS native)',
  // no crossDomainRedirect: no navigation — tokens are handed to native and the webview stays
  HandlerClass: Auth0PostLoginHandlerIos,
}, {
  handlerName: 'Android App (Webview)',
  handlerDescription: 'Handles post login events for Android app webview (fires a login complete event into native app)',
  // no crossDomainRedirect: no navigation — tokens are handed to native and the webview stays
  HandlerClass: Auth0PostLoginHandlerAndroid,
}, {
  handlerName: 'ShipStation API Dashboard',
  handlerDescription: 'Handles post login events for SS API Dash (redirects to the API Dash)',
  crossDomainRedirect: true, // redirects to the SS API Dash app, usually on another subdomain
  HandlerClass: Auth0PostLoginHandlerShipStationApiDash,
}, {
  // ShipStation Home is a broad catch-all for shipstation-tenant users — registered last among
  // non-default handlers so all client-specific handlers get priority when both would match.
  handlerName: 'ShipStation Home',
  handlerDescription: 'Handles post login logic for ShipStation apps (multi-partition redirect, cookie promotion, new user onboarding)',
  crossDomainRedirect: true, // redirects the user to their home partition, frequently another subdomain
  HandlerClass: Auth0PostLoginHandlerShipStationHome,
}, {
  handlerName: 'Default Handler',
  isDefault: true,
  handlerDescription: 'Generic post-login handler for apps without specialized requirements (simple redirect to targetUrl)',
  crossDomainRedirect: true, // redirects to the app's targetUrl, usually on another subdomain
  HandlerClass: Auth0PostLoginHandlerDefault,
}];

export default {
  registeredHandlers,
  handlerFromConfig: (handlerType, options) => {
    const handlerData = registeredHandlers.filter(h => h.HandlerClass === handlerType).pop();
    return new handlerData.HandlerClass({
      ...options,
      name: handlerData.name,
      description: handlerData.description,
    });
  },
};
